/**
 * depthFromVolume.js — НАСТОЯЩАЯ КАРТА ГЛУБИНЫ. Считается по объёму и по камере.
 * Mikhmel Cornerstone4D DICOM Viewer Pro — 10.19.18
 *
 * ПОВОД, и он прямой: в 10.19.17 я убрал подделку (яркость выдавалась за
 * глубину), и вместе с ней ушли затенение, отражения, туман и глубина резкости —
 * им нечем работать. Обещал сделать настоящую глубину отдельным заходом.
 *
 * ПОЧЕМУ НЕ ВЗЯТЬ ГОТОВУЮ У vtk.js. Проверено по исходникам версии, которая
 * лежит в проекте:
 *
 *   1. `Rendering/OpenGL/glsl/vtkVolumeFS.glsl.js` — в шейдере объёмного
 *      маппера НЕТ `gl_FragDepth`. Объём не пишет глубину вовсе; в аппаратном
 *      z-буфере для чисто объёмной сцены лежит дальняя плоскость.
 *   2. `Rendering/OpenGL/RenderWindow.js`, `get3DContext` — контекст создаётся
 *      без `antialias: false`, то есть буфер многосэмпловый. Скопировать из него
 *      глубину `blitFramebuffer`ом нельзя: перенос DEPTH из многосэмплового в
 *      обычный запрещён.
 *   3. `readPixels` с depth-вложения запрещён в WebGL2 в любом случае, а
 *      текстуры между разными контекстами не передаются.
 *
 * То есть «достать» глубину нечем — её надо ПОСЧИТАТЬ. Что и делается здесь:
 * свой контекст WebGL2, объём как трёхмерная текстура НЕПРОЗРАЧНОСТИ (та же
 * функция непрозрачности, что рисует картинку), луч на каждый пиксель, и
 * глубина — там, где накопленная непрозрачность переходит порог. Результат
 * читается в Float32Array и отдаётся кинематографическому рендереру через уже
 * существующий вход `setDepthSource` — поэтому оба движка (WebGL2 и WebGPU)
 * получают глубину без единой правки в них самих.
 *
 * ЧТО ЭТО СТОИТ. Глубина считается не каждый кадр, а когда сдвинулась камера
 * или сменился объём: в четверть разрешения окна, 160 шагов луча. Чтение
 * результата — RGBA8, то есть 4 байта на пиксель уменьшенного кадра (при окне
 * 700×900 это меньше 40 кБ).
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Это не второй рендерер: цвет и свет не считаются. Только
 * расстояние до первой значимой непрозрачности и — с 10.19.21 — НОРМАЛЬ в этой
 * точке: шесть дополнительных выборок объёма (центральные разности), перевод в
 * мир транспонированной матрицей «мир → индексы». Нормаль пишется во второе
 * вложение того же кадра (MRT) и читается вместе с глубиной; отдельного прохода
 * она не стоит. На сфере нормаль обязана совпасть с (точка − центр)/радиус —
 * это закреплено проверкой tests/normals_test.mjs (числами, без видеокарты)
 * и живой сверкой проверкаНормалейНаСфере() (на настоящем шейдере).
 */

import { log } from '../utils/logger.js';
import {
  разобратьКамеру, матрицыКамеры, мирВИндексы, вИндексы, направлениеВИндексы,
  лучЧерезПиксель, пересечьКоробку, нормировать, вычесть, длина, скалярно,
  трилинейно, градиентОбъёма, нормальИзГрадиента, попаданиеВСферу,
} from './depthMath.js';

/** Наибольшая сторона трёхмерной текстуры. Глубине высокое разрешение не нужно. */
export const МАКС_СТОРОНА = 192;

/**
 * [11.1.53] Сколько байт занимает СТРОКА текстуры R8 шириной dx при заданном
 * выравнивании. Вынесено отдельной функцией, потому что именно это число и
 * разошлось с размером массива: при выравнивании 4 и dx = 170 движку нужно 172
 * байта на строку. Проверяется числами в verify_11_1_53 — без браузера.
 *
 * @param {number} dx — ширина текстуры в пикселях (R8 = 1 байт на пиксель)
 * @param {number} выравнивание — UNPACK_ALIGNMENT: 1, 2, 4 или 8
 */
export function ЧИСЛО_БАЙТ_НА_СТРОКУ(dx, выравнивание = 1) {
  const a = Math.max(1, выравнивание | 0);
  return Math.ceil(dx / a) * a;
}
/** Доля разрешения окна, в которой считается глубина. */
export const ДОЛЯ_РАЗРЕШЕНИЯ = 0.5;
/** Шагов луча. Больше — точнее край, дороже кадр. */
export const ШАГОВ = 160;

const VERT = `#version 300 es
precision highp float;
in vec2 aPos;
out vec2 vUV;
void main() { vUV = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

/**
 * Проход глубины. Формулы — те же, что в depthMath.js, и это сверяется
 * проверкой tests/depth_test.mjs: расхождение между шейдером и проверяемой
 * математикой означало бы, что проверка ничего не проверяет.
 */
const FRAG = `#version 300 es
precision highp float;
precision highp sampler3D;
in vec2 vUV;
// [10.19.21] ДВА выхода одного прохода: глубина и нормаль. Нормаль — не второй
// проход и не вторая цена: те же лучи, тот же кадр, плюс шесть выборок объёма
// в точке попадания. Читаются оба вложения тем же readPixels.
layout(location = 0) out vec4 outDepth;
layout(location = 1) out vec4 outNormal;

uniform sampler3D uVolume;        // непрозрачность, 0…1
uniform mat4 uInvViewProj;
uniform mat3 uWorldToIndex;
uniform vec3 uVolumeOrigin;
uniform vec3 uDims;               // размер текстуры в вокселях
uniform vec3 uBoxMin;             // коробка объёма в ИНДЕКСАХ
uniform vec3 uBoxMax;
uniform vec3 uEye;
uniform vec3 uForward;
uniform float uNear;
uniform float uFar;
uniform float uHitAlpha;          // порог «здесь поверхность»
uniform int uSteps;

// Имена в шейдере латиницей НАРОЧНО: GLSL ES не допускает не-ASCII в
// идентификаторах, и первая версия этого файла не собралась именно из-за
// кириллических имён («ERROR: 0:22: '?' : syntax error»). Пояснения — по-русски,
// как во всём проекте; имена — латиницей, потому что иначе шейдера просто нет.
const float DEPTH_MISS = 1.0;
// Нормали нет: середина шкалы в rgb (декодируется в нулевой вектор) и ноль в
// альфе — признак «не годится». Просто нулей мало: 0 в rgb декодировался бы
// как вектор (−1,−1,−1), то есть как ЛОЖНАЯ нормаль.
const vec4 NO_NORMAL = vec4(0.5, 0.5, 0.5, 0.0);

// Выборка непрозрачности в НЕПРЕРЫВНЫХ индексах. Соответствие «индекс i —
// центр текселя i» даёт сдвиг +0.5; та же формула повторена числами в
// depthMath.js (трилинейно) — иначе проверку не с чем сверять.
float sampleA(vec3 idx, vec3 dims) {
  return texture(uVolume, (idx + 0.5) / dims).r;
}

/**
 * Упаковка 0…1 в RGBA8: точности одного байта на глубину не хватает.
 *
 * ВЕРХНЯЯ ГРАНИЦА НЕ 1,0, и это не педантизм. Схема упаковки построена на
 * fract(), а fract(1.0) = 0 — то есть ровно единица упаковывалась в (0,0,0,0) и
 * распаковывалась как НОЛЬ. Промах (глубина 1,0) превращался в «попадание у
 * самой ближней плоскости»: замер на живой сборке давал попаданий 100% при
 * силуэте картинки 33%, глубина 0,0 по всему полю вокруг анатомии, и я успел
 * заподозрить в этом и коробку объёма, и камеру, и функцию непрозрачности.
 * Виновата была одна константа.
 */
vec4 packDepth(float v) {
  v = clamp(v, 0.0, 0.99999994);
  vec4 e = vec4(1.0, 255.0, 65025.0, 16581375.0) * v;
  e = fract(e);
  e -= e.yzww * vec4(1.0 / 255.0, 1.0 / 255.0, 1.0 / 255.0, 0.0);
  return e;
}

void main() {
  // Луч через пиксель: точки NDC на ближней и дальней плоскостях переводятся в
  // мир обратной матрицей. Один код и для перспективы, и для параллельной.
  vec2 ndc = vUV * 2.0 - 1.0;
  vec4 nearH = uInvViewProj * vec4(ndc, -1.0, 1.0);
  vec4 farH  = uInvViewProj * vec4(ndc,  1.0, 1.0);
  vec3 p0 = nearH.xyz / nearH.w;
  vec3 p1 = farH.xyz / farH.w;
  vec3 dirWorld = normalize(p1 - p0);

  // Луч в индексы объёма: там коробка осевая, и пересечение считается плитами.
  vec3 ro = uWorldToIndex * (p0 - uVolumeOrigin);
  vec3 rd = uWorldToIndex * dirWorld;
  // ЛУЧ ВДОЛЬ ОСИ — отдельный случай, и на нём первая версия расчёта врала.
  //
  // Объёмное окно по умолчанию смотрит строго по оси (камера [0,−707,−0,5] на
  // цель [0, 0,5, −0,5]), то есть rd.x и rd.z равны нулю. Деление на нуль даёт
  // ±бесконечность, min/max её проглатывают, и плита по этой оси перестаёт
  // ограничивать: коробка объёма превращается в БЕСКОНЕЧНУЮ ПЛИТУ. Тогда луч,
  // проходящий далеко в стороне от объёма, считался вошедшим в него, попадал на
  // границу текстуры (CLAMP_TO_EDGE отдаёт краевой воксель) и «находил
  // поверхность» на нулевом расстоянии. Замер: попаданий 100% при силуэте
  // картинки 33%, глубина 0,0 по всему полю вокруг анатомии.
  //
  // В проверяемой математике (depthMath.js, пересечьКоробку) этот случай был
  // разобран с самого начала — расходились именно шейдер и проверка.
  bvec3 flat3 = lessThan(abs(rd), vec3(1e-8));
  bool missByAxis =
       (flat3.x && (ro.x < uBoxMin.x || ro.x > uBoxMax.x))
    || (flat3.y && (ro.y < uBoxMin.y || ro.y > uBoxMax.y))
    || (flat3.z && (ro.z < uBoxMin.z || ro.z > uBoxMax.z));
  if (missByAxis) { outDepth = packDepth(DEPTH_MISS); outNormal = NO_NORMAL; return; }
  vec3 safeRd = mix(rd, vec3(1e-8), vec3(flat3));

  vec3 t1v = (uBoxMin - ro) / safeRd;
  vec3 t2v = (uBoxMax - ro) / safeRd;
  vec3 tmin3 = min(t1v, t2v);
  vec3 tmax3 = max(t1v, t2v);
  float t0 = max(max(tmin3.x, tmin3.y), max(tmin3.z, 0.0));
  float t1 = min(min(tmax3.x, tmax3.y), tmax3.z);
  if (t1 <= t0) { outDepth = packDepth(DEPTH_MISS); outNormal = NO_NORMAL; return; }

  // ПОПАДАНИЕ — ЭТО ПЕРВАЯ ЗНАЧИМАЯ НЕПРОЗРАЧНОСТЬ, а не накопленная.
  //
  // Первая версия копила непрозрачность спереди назад (acc += (1−acc)·a) и
  // считала попаданием переход через порог. Замер на живой сборке показал, что
  // так попадание случается в 100% пикселей и у самой ближней плоскости: в
  // объёме 79% вокселей имеют непрозрачность выше 0,03, и за 160 шагов
  // накопление доходит до единицы даже в воздухе. Экранная картинка при этом
  // выглядит нормально, потому что маппер vtk.js берёт шаг ~0,5 мм и делит
  // непрозрачность на «единичное расстояние» (scalarOpacityUnitDistance) —
  // повторять всю эту модель ради глубины и подгонять её под маппер значит
  // получить ещё один правдоподобный, но чужой ответ.
  //
  // Для карты глубины нужно другое: РАССТОЯНИЕ ДО ВИДИМОЙ ПОВЕРХНОСТИ. Она там,
  // где вещество впервые становится существенно непрозрачным. Порог задаётся
  // долей от НАИБОЛЬШЕЙ непрозрачности этого объёма (считается при загрузке),
  // поэтому он не зависит ни от шага луча, ни от пресета: у прозрачных настроек
  // порог опускается вместе с ними.
  float stepLen = (t1 - t0) / float(uSteps);
  float tHit = -1.0;
  // Накопление accSoFar удалено [10.19.21]: оно копилось и НИКОГДА не читалось —
  // остаток от подхода, отвергнутого в 10.19.18 (см. пояснение выше). Мёртвый
  // код в цикле на 160 шагов — это и лишняя работа, и ложный намёк читателю,
  // будто накопление на что-то влияет.
  for (int i = 0; i < 512; i++) {
    if (i >= uSteps) break;
    float t = t0 + (float(i) + 0.5) * stepLen;
    vec3 idx = ro + safeRd * t;
    float a = sampleA(idx, uDims);
    if (a >= uHitAlpha) { tHit = t; break; }
  }
  if (tHit < 0.0) { outDepth = packDepth(DEPTH_MISS); outNormal = NO_NORMAL; return; }

  // Глубина — расстояние ВДОЛЬ ОСИ ВЗГЛЯДА (так устроен буфер глубины), и
  // нормируется ЛИНЕЙНО: тогда разности глубин пропорциональны миллиметрам, и у
  // порогов экранных эффектов появляется смысл.
  //
  // tHit измерен в ИНДЕКСАХ объёма, поэтому обратно в мир он переводится не
  // умножением на длину dirWorld (она равна 1), а отношением длин: сколько
  // миллиметров приходится на единицу индексного шага вдоль этого луча.
  float mmPerIndex = length(dirWorld) / max(length(safeRd), 1e-8);
  vec3 hit = p0 + dirWorld * (tHit * mmPerIndex);
  float alongView = dot(hit - uEye, uForward);
  float depth01 = clamp((alongView - uNear) / (uFar - uNear), 0.0, 1.0);
  outDepth = packDepth(depth01);

  // ── НОРМАЛЬ ИЗ ОБЪЁМА [10.19.21]: шесть выборок вокруг точки попадания. ──
  //
  // Градиент непрозрачности центральными разностями, шаг — один воксель
  // ПРОРЕЖЕННОЙ текстуры по каждой оси. Анизотропию шага здесь учитывать
  // нельзя: её выправляет перевод в мир ниже, и учёт в двух местах согнул бы
  // нормаль. Формулы повторены числами в depthMath.js (градиентОбъёма) и
  // сверяются проверкой tests/normals_test.mjs.
  vec3 hitIdx = ro + safeRd * tHit;
  vec3 g = vec3(
    sampleA(hitIdx + vec3(1.0, 0.0, 0.0), uDims) - sampleA(hitIdx - vec3(1.0, 0.0, 0.0), uDims),
    sampleA(hitIdx + vec3(0.0, 1.0, 0.0), uDims) - sampleA(hitIdx - vec3(0.0, 1.0, 0.0), uDims),
    sampleA(hitIdx + vec3(0.0, 0.0, 1.0), uDims) - sampleA(hitIdx - vec3(0.0, 0.0, 1.0), uDims));

  // Перевод градиента в МИР — ТРАНСПОНИРОВАННОЙ матрицей «мир → индексы»:
  // a — функция индексов, idx = A·(мир − origin), значит ∇мир = Aᵀ·∇инд.
  // В GLSL «вектор слева» (g * M) и есть умножение на транспонированную —
  // отдельной матрицы для этого не нужно. На чистом повороте Aᵀ = A⁻¹ и ошибка
  // не видна; ловится она на анизотропном шаге (0,7×0,7×2,5 мм у КТ), поэтому
  // проверка на сфере гоняет именно его.
  vec3 gWorld = g * uWorldToIndex;
  float gLen = length(gWorld);
  if (gLen < 1e-6) {
    // Градиент вырожден (плато непрозрачности): нормали нет, и честнее сказать
    // это альфой, чем выдумать вектор. Потребители при a=0 не считают N·L.
    outNormal = NO_NORMAL;
    return;
  }
  // Непрозрачность растёт ВНУТРЬ вещества → наружная нормаль = −градиент.
  // На сфере это (точка − центр)/радиус, что и закреплено проверкой.
  vec3 N = -gWorld / gLen;
  // Видимая сторона: на честном градиенте первой поверхности N уже смотрит к
  // камере (вдоль луча непрозрачность растёт, g·rd > 0, значит N·rd < 0), и
  // строка не срабатывает. Она выправляет только шумовые вырожденные случаи —
  // чтобы N·L не давал чёрных пятен там, где градиент дрогнул на кванте R8.
  if (dot(N, dirWorld) > 0.0) N = -N;
  outNormal = vec4(N * 0.5 + 0.5, 1.0);
}`;

/**
 * Матрица «мир → индексы ПРОРЕЖЕННОЙ текстуры»: к исходной добавляется деление
 * на шаг прореживания. [10.19.21] Вынесено из посчитать(): та же матрица нужна
 * отладке луча и проверке на сфере — считать её в трёх местах значит однажды
 * посчитать в трёх местах по-разному.
 * @returns {number[]} 3×3 по строкам
 */
function мир2индПрореженной(m, пр) {
  return [
    m[0] / пр[0], m[1] / пр[0], m[2] / пр[0],
    m[3] / пр[1], m[4] / пр[1], m[5] / пр[1],
    m[6] / пр[2], m[7] / пр[2], m[8] / пр[2],
  ];
}

function собрать(gl, тип, текст) {
  const ш = gl.createShader(тип);
  gl.shaderSource(ш, текст);
  gl.compileShader(ш);
  if (!gl.getShaderParameter(ш, gl.COMPILE_STATUS)) {
    const лог = gl.getShaderInfoLog(ш);
    gl.deleteShader(ш);
    throw new Error('шейдер глубины не собрался: ' + лог);
  }
  return ш;
}

/**
 * Расчёт глубины по объёму. Живёт в своём контексте WebGL2: чужой трогать нельзя,
 * а свой можно уничтожить, не задев ни Cornerstone, ни кинематографический слой.
 */
export class ГлубинаИзОбъёма {
  constructor() {
    this._canvas = null;
    this._gl = null;
    this._prog = null;
    this._tex = null;
    this._fbo = null;
    this._цвет = null;
    this._w = 0; this._h = 0;
    this._dims = [0, 0, 0];
    this._буфер = null;
    this._глубина = null;
    this._подпись = '';       // подпись объёма: чтобы не грузить его дважды
    this._причина = '';
  }

  причина() { return this._причина; }
  готов() { return !!(this._gl && this._prog && this._tex); }

  _поднятьКонтекст() {
    if (this._gl) return true;
    try {
      const c = document.createElement('canvas');
      c.width = 8; c.height = 8;
      const gl = c.getContext('webgl2', {
        alpha: false, antialias: false, depth: false, stencil: false,
        preserveDrawingBuffer: false, powerPreference: 'high-performance',
      });
      if (!gl) { this._причина = 'WebGL2 недоступен — глубину считать нечем'; return false; }
      const vs = собрать(gl, gl.VERTEX_SHADER, VERT);
      const fs = собрать(gl, gl.FRAGMENT_SHADER, FRAG);
      const p = gl.createProgram();
      gl.attachShader(p, vs); gl.attachShader(p, fs); gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        this._причина = 'программа глубины не связалась: ' + gl.getProgramInfoLog(p);
        return false;
      }
      gl.deleteShader(vs); gl.deleteShader(fs);

      const vao = gl.createVertexArray();
      gl.bindVertexArray(vao);
      const vbo = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(p, 'aPos');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      gl.bindVertexArray(null);

      this._canvas = c; this._gl = gl; this._prog = p; this._vao = vao;
      return true;
    } catch (e) {
      this._причина = 'контекст глубины не поднялся: ' + e.message;
      log.warn('[Глубина]', e.message);
      return false;
    }
  }

  /**
   * Загрузить объём как текстуру НЕПРОЗРАЧНОСТИ.
   *
   * Непрозрачность, а не плотность: тогда порог попадания означает ровно то же,
   * что видно на экране. Функцию непрозрачности берём у самого объёмного окна —
   * иначе глубина считалась бы по другой картинке, чем показана.
   *
   * @param {{скаляры: ArrayLike<number>, dims: number[], spacing: number[],
   *          origin: number[], direction: number[]|null,
   *          непрозрачность: (значение: number) => number, подпись: string}} о
   */
  загрузитьОбъём(о) {
    if (!this._поднятьКонтекст()) return { ок: false, причина: this._причина };
    const { скаляры, dims, spacing, origin } = о;
    if (!скаляры || !dims || dims.length !== 3) return { ок: false, причина: 'объём без данных или без размеров' };
    if (о.подпись && о.подпись === this._подпись) return { ок: true, повтор: true };
    if (typeof о.непрозрачность !== 'function') {
      return { ок: false, причина: 'объёмное окно не отдало функцию непрозрачности — считать глубину по чужой картинке нельзя' };
    }

    const [nx, ny, nz] = dims.map((d) => Math.max(1, d | 0));
    const шагX = Math.max(1, Math.ceil(nx / МАКС_СТОРОНА));
    const шагY = Math.max(1, Math.ceil(ny / МАКС_СТОРОНА));
    const шагZ = Math.max(1, Math.ceil(nz / МАКС_СТОРОНА));
    const dx = Math.max(1, Math.floor(nx / шагX));
    const dy = Math.max(1, Math.floor(ny / шагY));
    const dz = Math.max(1, Math.floor(nz / шагZ));

    // Прореживание берёт МАКСИМУМ по блоку, а не среднее: усреднение размывает
    // тонкую кость до прозрачности, и поверхность уезжает внутрь.
    const данные = new Uint8Array(dx * dy * dz);
    for (let z = 0; z < dz; z++) {
      const z0 = z * шагZ;
      for (let y = 0; y < dy; y++) {
        const y0 = y * шагY;
        for (let x = 0; x < dx; x++) {
          const x0 = x * шагX;
          let макс = 0;
          for (let бz = 0; бz < шагZ; бz++) {
            const zz = z0 + бz; if (zz >= nz) break;
            for (let бy = 0; бy < шагY; бy++) {
              const yy = y0 + бy; if (yy >= ny) break;
              const строка = (zz * ny + yy) * nx;
              for (let бx = 0; бx < шагX; бx++) {
                const xx = x0 + бx; if (xx >= nx) break;
                // Раньше здесь стояло `if (v > макс || макс === 0)`: значение
                // вокселя сравнивалось с НЕПРОЗРАЧНОСТЬЮ — величины разных
                // единиц. Работало случайно; убрано.
                const a = о.непрозрачность(скаляры[строка + xx]);
                if (a > макс) макс = a;
              }
            }
          }
          данные[(z * dy + y) * dx + x] = Math.max(0, Math.min(255, Math.round(макс * 255)));
        }
      }
    }

    const gl = this._gl;
    if (this._tex) gl.deleteTexture(this._tex);
    this._tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_3D, this._tex);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    // [11.1.53] ВОТ ПОЧЕМУ ГЛУБИНА БЫЛА ПУСТА. В журнале владельца стояло
    // дословно: «WebGL: INVALID_OPERATION: texImage3D: ArrayBufferView not big
    // enough for request», а строкой ниже — «луч попал в объём в 0.00%
    // пикселей». Второе выглядело самостоятельной причиной, но это следствие:
    // загрузка текстуры провалилась, и шейдер маршировал по пустоте.
    //
    // Дело в UNPACK_ALIGNMENT. По умолчанию он равен 4: WebGL считает, что
    // каждая СТРОКА пикселей выровнена на 4 байта. У формата R8 строка — это
    // ровно dx байт, и пока dx кратен четырём, всё сходится. На КТ владельца
    // объём 512×512×818 прореживается в 170×170×163, а 170 % 4 = 2. Движок
    // требует 172 байта на строку и просит 172×170×163 = 4 766 120 байт,
    // тогда как в массиве их 170×170×163 = 4 710 700. Не хватает ровно
    // 2 × 170 × 163 = 55 420 — байты выравнивания, которых мы не кладём и
    // класть не собираемся.
    //
    // Правильный ответ — сказать движку, что выравнивания нет: строки идут
    // подряд. Подгонять размеры текстуры под кратность четырём было бы хуже —
    // это меняло бы геометрию прореживания ради формата загрузки.
    //
    // Ошибка вылезала не на всяком исследовании: 512 при шаге 2 даёт 256
    // (кратно 4) и всё работало. Отсюда и «то есть глубина, то нет».
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage3D(gl.TEXTURE_3D, 0, gl.R8, dx, dy, dz, 0, gl.RED, gl.UNSIGNED_BYTE, данные);
    // Молчаливого провала здесь больше не будет: WebGL о своих отказах
    // сообщает только через getError, и раньше его никто не спрашивал —
    // сообщение в консоли писал сам браузер, а программа считала загрузку
    // удавшейся и уходила считать глубину по пустой текстуре.
    const ошГл = gl.getError();
    if (ошГл !== gl.NO_ERROR) {
      const нужно = ЧИСЛО_БАЙТ_НА_СТРОКУ(dx) * dy * dz;
      this._причина = `текстура глубины не загрузилась (код WebGL ${ошГл}): текстура ${dx}×${dy}×${dz}, `
        + `в массиве ${данные.length} байт, движку нужно ${нужно}`;
      log.warn('[Глубина] ' + this._причина);
      return { ок: false, причина: this._причина };
    }

    this._dims = [dx, dy, dz];
    // [10.19.21] Прореженный объём остаётся на руках: отладкаЛуча теперь марширует
    // по нему НА ПРОЦЕССОРЕ теми же формулами, что шейдер, и отдаёт попадание и
    // нормаль числами. Цена — не выше 192³ байт (7 МБ) на единственный экземпляр;
    // именно так в 10.19.18 нашлась бесконечная плита, споря было бы бесполезно.
    this._данные = данные;
    this._прореживание = [шагX, шагY, шагZ];
    this._исходныеDims = [nx, ny, nz];
    this._spacing = [spacing[0], spacing[1], spacing[2]];
    this._origin = [origin[0], origin[1], origin[2]];
    this._direction = о.direction && о.direction.length === 9 ? Array.from(о.direction) : null;
    this._подпись = о.подпись || '';
    this._причина = '';

    // Доля непустых вокселей — не украшение: если она ноль, значит функция
    // непрозрачности к этим данным не подходит, и глубина будет пустой. Лучше
    // сказать это сразу, чем отдать карту промахов и делать вид, что всё хорошо.
    let непустых = 0, максБайт = 0;
    for (let i = 0; i < данные.length; i++) { if (данные[i] > 8) непустых++; if (данные[i] > максБайт) максБайт = данные[i]; }
    const доля = непустых / данные.length;
    // Наибольшая непрозрачность объёма: от неё берётся порог поверхности,
    // поэтому прозрачные пресеты не остаются без глубины вовсе.
    this._максНепрозрачность = максБайт / 255;
    log.info(`[Глубина] объём ${nx}×${ny}×${nz} → текстура ${dx}×${dy}×${dz}, непрозрачных вокселей ${(доля * 100).toFixed(1)}%`);
    if (доля < 0.0005) {
      this._причина = `функция непрозрачности даёт почти пустой объём (${(доля * 100).toFixed(2)}% вокселей) — глубину считать не по чему`;
      return { ок: false, причина: this._причина, доля };
    }
    return { ок: true, dims: this._dims, доля };
  }

  /**
   * Посчитать карту глубины для камеры окна.
   *
   * @param {{камера: object, ширина: number, высота: number, порог?: number}} о
   * @returns {{ок: true, данные: Float32Array, width: number, height: number,
   *            near: number, far: number, попаданий: number} |
   *           {ок: false, причина: string}}
   */
  посчитать(о) {
    if (!this.готов()) return { ок: false, причина: this._причина || 'объём в расчёт глубины не загружен' };
    const к = разобратьКамеру(о.камера, (о.ширина || 1) / (о.высота || 1));
    if (!к.ок) return { ок: false, причина: к.причина };
    const преобр = мирВИндексы(this._origin, this._spacing, this._direction);
    if (!преобр) return { ок: false, причина: 'шаг объёма нулевой — перевод в индексы невозможен' };

    const w = Math.max(8, Math.round((о.ширина || 256) * ДОЛЯ_РАЗРЕШЕНИЯ));
    const h = Math.max(8, Math.round((о.высота || 256) * ДОЛЯ_РАЗРЕШЕНИЯ));
    const gl = this._gl;
    if (w !== this._w || h !== this._h) this._пересоздатьЦель(w, h);

    const м = матрицыКамеры(к);
    const [dx, dy, dz] = this._dims;

    // Коробка объёма в индексах прореженной текстуры.
    const boxMax = [dx - 1, dy - 1, dz - 1];

    // Матрица «мир → индексы прореженной текстуры»: к исходной добавляется
    // деление на шаг прореживания, иначе луч пойдёт по другой сетке.
    const пр = this._прореживание;
    const m = преобр.матрица;
    const мир2инд = [
      m[0] / пр[0], m[1] / пр[0], m[2] / пр[0],
      m[3] / пр[1], m[4] / пр[1], m[5] / пр[1],
      m[6] / пр[2], m[7] / пр[2], m[8] / пр[2],
    ];
    // GL ждёт mat3 по СТОЛБЦАМ, а у нас по строкам — транспонируем здесь, один раз.
    const мир2индGL = new Float32Array([
      мир2инд[0], мир2инд[3], мир2инд[6],
      мир2инд[1], мир2инд[4], мир2инд[7],
      мир2инд[2], мир2инд[5], мир2инд[8],
    ]);


    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbo);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this._prog);
    gl.bindVertexArray(this._vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_3D, this._tex);

    const U = (имя) => gl.getUniformLocation(this._prog, имя);
    gl.uniform1i(U('uVolume'), 0);
    gl.uniformMatrix4fv(U('uInvViewProj'), false, м.invViewProj);
    gl.uniformMatrix3fv(U('uWorldToIndex'), false, мир2индGL);
    gl.uniform3f(U('uVolumeOrigin'), this._origin[0], this._origin[1], this._origin[2]);
    gl.uniform3f(U('uDims'), dx, dy, dz);
    gl.uniform3f(U('uBoxMin'), 0, 0, 0);
    gl.uniform3f(U('uBoxMax'), boxMax[0], boxMax[1], boxMax[2]);
    gl.uniform3f(U('uEye'), к.eye[0], к.eye[1], к.eye[2]);
    gl.uniform3f(U('uForward'), к.forward[0], к.forward[1], к.forward[2]);
    gl.uniform1f(U('uNear'), к.near);
    gl.uniform1f(U('uFar'), к.far);
    // Порог поверхности — доля от НАИБОЛЬШЕЙ непрозрачности этого объёма.
    // Абсолютное значение здесь не годится: у «МР обзор» вся картинка живёт в
    // непрозрачностях 0,05…0,2, и любой фиксированный порог либо ловил бы
    // воздух, либо не находил бы ничего.
    const порог = о.порог > 0 ? о.порог
      : Math.max(0.05, (this._максНепрозрачность || 1) * (о.доляПорога ?? 0.35));
    this._последнийПорог = порог;
    gl.uniform1f(U('uHitAlpha'), порог);
    gl.uniform1i(U('uSteps'), ШАГОВ);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Чтение — сразу, в том же кадре: цель наша, никто её не очистит.
    if (!this._буфер || this._буфер.length !== w * h * 4) this._буфер = new Uint8Array(w * h * 4);
    let сброс = 0;
    while (gl.getError() !== gl.NO_ERROR && сброс++ < 32) { /* чужие ошибки в очереди */ }
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, this._буфер);
    const ош = gl.getError();
    if (ош !== gl.NO_ERROR) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      return { ок: false, причина: `чтение карты глубины дало ошибку gl ${ош}` };
    }

    // [10.19.21] Второе вложение — нормали. Читается тем же способом; если
    // чтение не удалось, глубина всё равно отдаётся, а нормалей честно нет:
    // эффекты по нормалям тогда не включаются, вместо того чтобы считать по мусору.
    if (!this._буферНормалей || this._буферНормалей.length !== w * h * 4) {
      this._буферНормалей = new Uint8Array(w * h * 4);
    }
    gl.readBuffer(gl.COLOR_ATTACHMENT1);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, this._буферНормалей);
    const ошНорм = gl.getError();
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const нормалиЕсть = ошНорм === gl.NO_ERROR;

    if (!this._глубина || this._глубина.length !== w * h) this._глубина = new Float32Array(w * h);
    let попаданий = 0, сНормалью = 0;
    for (let i = 0, j = 0; i < this._глубина.length; i++, j += 4) {
      // Распаковка RGBA8 → 0…1 (обратная упаковке в шейдере).
      const v = this._буфер[j] / 255 + this._буфер[j + 1] / 65025
        + this._буфер[j + 2] / 16581375 + this._буфер[j + 3] / 4228250625;
      this._глубина[i] = v;
      if (v < 0.999) {
        попаданий++;
        if (нормалиЕсть && this._буферНормалей[j + 3] > 127) сНормалью++;
      }
    }
    return {
      ок: true, данные: this._глубина, width: w, height: h,
      near: к.near, far: к.far, попаданий, доляПопаданий: попаданий / (w * h),
      // Нормали — сырым RGBA8 (n·0,5+0,5, годность в альфе): рендереру ровно в
      // таком виде и грузить, распаковка во Float здесь была бы работой впустую.
      нормали: нормалиЕсть ? this._буферНормалей : null,
      доляНормалей: попаданий > 0 && нормалиЕсть ? сНормалью / попаданий : 0,
    };
  }

  /**
   * Отладка одного луча — теми же формулами, что в depthMath.js.
   *
   * Нужна не «на всякий случай»: когда шейдер и проверяемая математика
   * расходятся, спорить бесполезно, надо сравнить числа на одном и том же
   * пикселе. Именно так нашлось, что коробка объёма в шейдере превращалась в
   * бесконечную плиту при луче вдоль оси.
   *
   * @param {{камера: object, ширина: number, высота: number, ndcX: number, ndcY: number}} о
   */
  отладкаЛуча(о) {
    const к = разобратьКамеру(о.камера, (о.ширина || 1) / (о.высота || 1));
    if (!к.ок) return { ок: false, причина: к.причина };
    const преобр = мирВИндексы(this._origin, this._spacing, this._direction);
    if (!преобр) return { ок: false, причина: 'перевод в индексы невозможен' };
    const м = матрицыКамеры(к);
    const л = лучЧерезПиксель(м.invViewProj, о.ndcX ?? 0, о.ndcY ?? 0);
    const пр = this._прореживание || [1, 1, 1];
    const ro = вИндексы(преобр, л.начало).map((v, i) => v / пр[i]);
    const rd = направлениеВИндексы(преобр, л.направление).map((v, i) => v / пр[i]);
    const [dx, dy, dz] = this._dims;
    const пересечение = пересечьКоробку(ro, rd, [0, 0, 0], [dx - 1, dy - 1, dz - 1]);

    // [10.19.21] Марш по объёму НА ПРОЦЕССОРЕ — теми же формулами, что шейдер:
    // тот же шаг, тот же критерий попадания, те же шесть выборок градиента и та
    // же транспонированная матрица. Когда нормаль на экране выглядит криво,
    // сравнивать надо числа в ОДНОМ пикселе, а не спорить о картинке.
    let попадание = null;
    if (пересечение && this._данные) {
      const порог = this._последнийПорог || 0.5;
      const мИнд = мир2индПрореженной(преобр.матрица, пр);
      for (let i = 0; i < ШАГОВ; i++) {
        const t = пересечение.вход + (i + 0.5) * (пересечение.выход - пересечение.вход) / ШАГОВ;
        const idx = [ro[0] + rd[0] * t, ro[1] + rd[1] * t, ro[2] + rd[2] * t];
        const a = трилинейно(this._данные, this._dims, idx) / 255;
        if (a >= порог) {
          const г = градиентОбъёма(this._данные, this._dims, idx, 1).map((v) => v / 255);
          const N = нормальИзГрадиента({ матрица: мИнд }, г);
          const ммНаИндекс = длина(л.направление) / Math.max(длина(rd), 1e-8);
          const мир = л.начало.map((v, k) => v + л.направление[k] * t * ммНаИндекс);
          попадание = {
            t: +t.toFixed(2),
            индексы: idx.map((v) => +v.toFixed(2)),
            мир: мир.map((v) => +v.toFixed(2)),
            непрозрачность: +a.toFixed(3),
            градиентИндексы: г.map((v) => +v.toFixed(4)),
            нормаль: N ? N.map((v) => +v.toFixed(4)) : null,
          };
          break;
        }
      }
    }
    return {
      ок: true,
      начало: л.начало.map((v) => +v.toFixed(2)),
      направление: л.направление.map((v) => +v.toFixed(4)),
      roИндексы: ro.map((v) => +v.toFixed(2)),
      rdИндексы: rd.map((v) => +v.toFixed(4)),
      коробка: [0, 0, 0, dx - 1, dy - 1, dz - 1],
      пересечение: пересечение ? { вход: +пересечение.вход.toFixed(2), выход: +пересечение.выход.toFixed(2) } : null,
      попадание,
      near: к.near, far: к.far, порог: this._последнийПорог,
      максНепрозрачность: this._максНепрозрачность,
      origin: this._origin, spacing: this._spacing, direction: this._direction,
    };
  }

  _пересоздатьЦель(w, h) {
    const gl = this._gl;
    if (this._fbo) gl.deleteFramebuffer(this._fbo);
    if (this._цвет) gl.deleteTexture(this._цвет);
    if (this._нормаль) gl.deleteTexture(this._нормаль);
    this._canvas.width = w; this._canvas.height = h;
    const текстура = () => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      return t;
    };
    this._цвет = текстура();
    // [10.19.21] Второе вложение — нормали (n·0,5+0,5 в rgb, годность в альфе).
    // Тот же кадр, те же лучи: нормаль не стоит отдельного прохода.
    this._нормаль = текстура();
    this._fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this._цвет, 0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this._нормаль, 0);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this._w = w; this._h = h;
  }

  освободить() {
    const gl = this._gl;
    if (!gl) return;
    try {
      if (this._tex) gl.deleteTexture(this._tex);
      if (this._цвет) gl.deleteTexture(this._цвет);
      if (this._нормаль) gl.deleteTexture(this._нормаль);
      if (this._fbo) gl.deleteFramebuffer(this._fbo);
      if (this._prog) gl.deleteProgram(this._prog);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    } catch (e) { log.debug('[Глубина/освободить]', e.message); }
    this._tex = null; this._цвет = null; this._нормаль = null; this._fbo = null; this._prog = null;
    this._gl = null; this._canvas = null; this._подпись = ''; this._данные = null;
  }
}

/**
 * Функция непрозрачности объёмного окна как обычная функция значения.
 *
 * Берётся у самого окна (vtkPiecewiseFunction). Если окно её не отдаёт — здесь
 * возвращается null, и выше это становится названной причиной отказа: считать
 * глубину по выдуманной непрозрачности значит снова получить правдоподобную
 * ложь, только другого сорта.
 */
export function непрозрачностьОкна(vp) {
  try {
    const prop = vp?.getDefaultActor?.()?.actor?.getProperty?.();
    const of = prop?.getScalarOpacity?.(0);
    if (!of) return null;
    // Диапазон узлов: за его пределами непрозрачность постоянна.
    const узлов = of.getSize?.() ?? 0;
    if (!узлов) return null;
    const первый = [0, 0, 0, 0]; const последний = [0, 0, 0, 0];
    of.getNodeValue?.(0, первый);
    of.getNodeValue?.(узлов - 1, последний);
    const низ = первый[0], верх = последний[0];
    if (!(верх > низ)) return null;

    // Таблица на 256 значений: вызывать getValue на каждый воксель дорого.
    const таблица = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = низ + (верх - низ) * (i / 255);
      let a = 0;
      if (typeof of.getValue === 'function') a = of.getValue(x);
      else {
        // Запасной путь: линейная интерполяция по узлам.
        let пред = первый, тек = [0, 0, 0, 0];
        for (let n = 0; n < узлов; n++) {
          of.getNodeValue(n, тек);
          if (тек[0] >= x) {
            const шир = тек[0] - пред[0];
            a = шир > 1e-9 ? пред[1] + (тек[1] - пред[1]) * ((x - пред[0]) / шир) : тек[1];
            break;
          }
          пред = тек.slice();
          a = тек[1];
        }
      }
      таблица[i] = Math.max(0, Math.min(1, a));
    }
    const шкала = 255 / (верх - низ);
    const функция = (значение) => {
      const i = (значение - низ) * шкала;
      return таблица[i < 0 ? 0 : (i > 255 ? 255 : i | 0)];
    };
    // Область определения — наружу: по ней видно, в тех же единицах заданы
    // данные объёма или в других. Без этого «воздух непрозрачен» выглядит как
    // загадка, а не как несовпадение шкал.
    функция.область = [низ, верх];
    функция.доляНепрозрачных = таблица.reduce((с, a) => с + (a > 0.03 ? 1 : 0), 0) / 256;
    return функция;
  } catch (e) {
    log.debug('[Глубина/непрозрачность]', e.message);
    return null;
  }
}

/**
 * ЖИВАЯ СВЕРКА НА СФЕРЕ [10.19.21]: нормаль обязана совпасть с
 * (точка − центр)/радиус.
 *
 * Проверка в tests/normals_test.mjs гоняет ТЕ ЖЕ формулы числами, но без
 * видеокарты — а расходились у нас в 10.19.18 именно шейдер и математика
 * (бесконечная плита). Поэтому здесь та же сфера прогоняется через НАСТОЯЩИЙ
 * шейдер: синтетический объём, параллельная камера, чтение карты нормалей и
 * угол до аналитической нормали в каждом пикселе.
 *
 * Шаг сетки АНИЗОТРОПНЫЙ нарочно (1×1×2,5 мм): на чистом повороте
 * транспонированная матрица совпадает с обратной, и подмена одной другою не
 * видна; анизотропия — единственное, что её ловит.
 *
 * Работает на СВОЁМ экземпляре и освобождает его: рабочий расчёт глубины,
 * его подпись объёма и его текстуры не трогаются вовсе.
 *
 * @returns {{ок: boolean, причина?: string, пикселей?: number, попаданий?: number,
 *            среднийГрадус?: number, p95Градус?: number, максГрадус?: number,
 *            совпалоСилуэтов?: number, мс?: number}}
 */
export function проверкаНормалейНаСфере(о = {}) {
  const N = о.сторона ?? 96;
  const R = о.радиус ?? 30;                 // мм
  // Ширина рампы ≥ двух наибольших шагов сетки: трафарет разностей (±1 воксель,
  // по Z это ±2,5 мм) обязан сидеть в линейной зоне перехода. Рампа уже
  // трафарета — предел метода: замер в tests/normals_test.mjs даёт средний
  // 5,3° на рампе 3 мм против 1,6° на рампе 6 мм при том же коде.
  const w = о.ширинаРампы ?? 6;             // мм: непрозрачность 1 → 0 на этой толщине
  const spacing = о.spacing ?? [1, 1, 2.5]; // анизотропия НАРОЧНО — см. выше
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());

  // Центр сферы — в мировом нуле: origin подбирается под середину сетки.
  const origin = [
    -((N - 1) / 2) * spacing[0],
    -((N - 1) / 2) * spacing[1],
    -((N - 1) / 2) * spacing[2],
  ];
  const скаляры = new Float32Array(N * N * N);
  for (let z = 0; z < N; z++) {
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        const мир = [origin[0] + x * spacing[0], origin[1] + y * spacing[1], origin[2] + z * spacing[2]];
        const r = Math.sqrt(мир[0] * мир[0] + мир[1] * мир[1] + мир[2] * мир[2]);
        скаляры[(z * N + y) * N + x] = Math.max(0, Math.min(1, (R - r) / w));
      }
    }
  }

  const расчёт = new ГлубинаИзОбъёма();
  try {
    const загр = расчёт.загрузитьОбъём({
      скаляры, dims: [N, N, N], spacing, origin, direction: null,
      непрозрачность: (v) => v, подпись: 'сверка-нормалей-сфера',
    });
    if (!загр.ок) return { ок: false, причина: загр.причина };

    const камера = {
      position: [0, 0, 300], focalPoint: [0, 0, 0], viewUp: [0, 1, 0],
      parallelProjection: true, parallelScale: R * 2, clippingRange: [200, 400],
    };
    // Порог 0,5 задан явно: изоповерхность a=0,5 у рампы шириной w лежит на
    // радиусе R − w/2 — с ним и сверяемся.
    const карта = расчёт.посчитать({ камера, ширина: 256, высота: 256, порог: 0.5 });
    if (!карта.ок) return { ок: false, причина: карта.причина };
    if (!карта.нормали) return { ок: false, причина: 'карта нормалей не прочиталась' };

    const к = разобратьКамеру(камера, 1);
    const м = матрицыКамеры(к);
    const Rпов = R - w / 2;
    const углы = [];
    let попаданий = 0, совпало = 0, безНормали = 0;
    for (let py = 0; py < карта.height; py++) {
      for (let px = 0; px < карта.width; px++) {
        const i = py * карта.width + px;
        const gpuПопал = карта.данные[i] < 0.999;
        // readPixels отдаёт строки СНИЗУ ВВЕРХ: py=0 — это ndcY=−1.
        const ndcX = ((px + 0.5) / карта.width) * 2 - 1;
        const ndcY = ((py + 0.5) / карта.height) * 2 - 1;
        const л = лучЧерезПиксель(м.invViewProj, ndcX, ndcY);
        const t = попаданиеВСферу(л.начало, л.направление, [0, 0, 0], Rпов);
        if (gpuПопал === (t !== null)) совпало++;
        if (!gpuПопал || t === null) continue;
        попаданий++;
        const j = i * 4;
        if (карта.нормали[j + 3] < 128) { безНормали++; continue; }
        const n = [
          карта.нормали[j] / 255 * 2 - 1,
          карта.нормали[j + 1] / 255 * 2 - 1,
          карта.нормали[j + 2] / 255 * 2 - 1,
        ];
        const nГПУ = нормировать(n);
        const P = [л.начало[0] + л.направление[0] * t, л.начало[1] + л.направление[1] * t, л.начало[2] + л.направление[2] * t];
        const nОпора = нормировать(P); // центр в нуле: (точка − центр)/радиус
        const кос = Math.max(-1, Math.min(1, скалярно(nГПУ, nОпора)));
        углы.push(Math.acos(кос) * 180 / Math.PI);
      }
    }
    if (!углы.length) return { ок: false, причина: 'ни одного пикселя для сверки — попаданий нет' };
    углы.sort((a, b) => a - b);
    const средний = углы.reduce((с, a) => с + a, 0) / углы.length;
    const p95 = углы[Math.min(углы.length - 1, Math.floor(углы.length * 0.95))];
    const макс = углы[углы.length - 1];
    const итог = {
      // Порог по среднему и p95, а не по максимуму: у самого силуэта луч скользит
      // по касательной, и пара граничных пикселей всегда шумит квантом R8.
      ок: средний < 3 && p95 < 10,
      пикселей: карта.width * карта.height,
      попаданий,
      совпалоСилуэтов: +(совпало / (карта.width * карта.height)).toFixed(4),
      безНормали,
      среднийГрадус: +средний.toFixed(2),
      p95Градус: +p95.toFixed(2),
      максГрадус: +макс.toFixed(2),
      мс: +(((typeof performance !== 'undefined' ? performance.now() : Date.now())) - t0).toFixed(0),
    };
    log.info(`[Глубина/сверка] сфера: попаданий ${попаданий}, средний угол ${итог.среднийГрадус}°, `
      + `p95 ${итог.p95Градус}°, макс ${итог.максГрадус}°, силуэты ${(итог.совпалоСилуэтов * 100).toFixed(1)}%`);
    return итог;
  } catch (e) {
    return { ок: false, причина: 'сверка на сфере упала: ' + e.message };
  } finally {
    расчёт.освободить();
  }
}

export default { ГлубинаИзОбъёма, непрозрачностьОкна, проверкаНормалейНаСфере, МАКС_СТОРОНА, ДОЛЯ_РАЗРЕШЕНИЯ, ШАГОВ };
